/* UDK shared Klaviyo capture utilities.
   Used by: sections/udk-mystery-popup.liquid, sections/udk-discount-signup.liquid.
   Loaded as a blocking script tag from each section (tiny file, guarded against
   double-execution) so it's guaranteed available before section JS runs.

   Klaviyo notes:
   - Client Subscriptions API, public key auth (safe to expose).
   - revision MUST be >= 2025-04-15: older revisions reject the `subscriptions`
     consent field with a silent 400.
   - Klaviyo 202s bogus phone numbers and drops them async — client-side
     validation is the only real gate.
   Non-happy paths:
   - 4xx  -> user-fixable (bad email/phone): returned to caller for inline UI
   - 5xx / network / timeout -> auto-queued in a localStorage outbox and
     retried on the next page load, so a flaky connection never loses a signup
   - keepalive:true so navigating away mid-POST doesn't cancel it */
(function () {
  if (window.UDKCapture) return;

  var API_REVISION = '2025-04-15';
  var STORE_KEY = 'udkMysteryPopup.v1'; // shared funnel state (popup + inline)
  var OUTBOX_KEY = 'udkMysteryPopup.outbox';

  function readState() {
    try { return JSON.parse(localStorage.getItem(STORE_KEY)) || {}; } catch (e) { return {}; }
  }

  function writeState(patch) {
    var s = readState();
    for (var k in patch) s[k] = patch[k];
    try { localStorage.setItem(STORE_KEY, JSON.stringify(s)); } catch (e) {}
  }

  function post(url, payload) {
    var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, 10000) : null;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: API_REVISION },
      body: JSON.stringify(payload),
      keepalive: true,
      signal: ctrl ? ctrl.signal : undefined
    }).then(function (res) {
      if (timer) clearTimeout(timer);
      if (res.ok) return { ok: true };
      if (res.status >= 500) return { ok: false, retryable: true, status: res.status };
      return res.json().then(function (body) {
        var detail = body && body.errors && body.errors[0] && body.errors[0].detail;
        return { ok: false, retryable: false, status: res.status, detail: detail || '' };
      }, function () {
        return { ok: false, retryable: false, status: res.status, detail: '' };
      });
    }).catch(function () {
      if (timer) clearTimeout(timer);
      return { ok: false, retryable: true, network: true };
    });
  }

  function loadOutbox() {
    try { return JSON.parse(localStorage.getItem(OUTBOX_KEY)) || []; } catch (e) { return []; }
  }
  function saveOutbox(q) {
    try { localStorage.setItem(OUTBOX_KEY, JSON.stringify(q.slice(0, 10))); } catch (e) {}
  }
  function enqueue(url, payload) {
    var q = loadOutbox();
    q.push({ url: url, payload: payload });
    saveOutbox(q);
  }
  function flushOutbox() {
    var q = loadOutbox();
    if (!q.length) return;
    saveOutbox([]);
    q.forEach(function (item) {
      if (!item || !item.url) return; // drop legacy/corrupt entries
      post(item.url, item.payload).then(function (r) {
        if (!r.ok && r.retryable) enqueue(item.url, item.payload); // keep for the next visit
      });
    });
  }

  /* Subscribe a profile to a list. Retryable failures are auto-queued —
     callers can treat `ok || retryable` as "captured". */
  function subscribe(opts) {
    var url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(opts.company);
    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: opts.source, // per-touchpoint attribution ($source in Klaviyo)
          profile: { data: { type: 'profile', attributes: opts.profile } }
        },
        relationships: { list: { data: { type: 'list', id: opts.list } } }
      }
    };
    return post(url, payload).then(function (r) {
      if (!r.ok && r.retryable) enqueue(url, payload);
      return r;
    });
  }

  /* Country data sourced from the intl-tel-input project (iso2, dial code, name).
     Flag emoji are derived from the iso2 code — no image assets needed. */
  var COUNTRIES = [["us","1","United States"],["af","93","Afghanistan"],["al","355","Albania"],["dz","213","Algeria"],["as","1","American Samoa"],["ad","376","Andorra"],["ao","244","Angola"],["ai","1","Anguilla"],["ag","1","Antigua and Barbuda"],["ar","54","Argentina"],["am","374","Armenia"],["aw","297","Aruba"],["ac","247","Ascension Island"],["au","61","Australia"],["at","43","Austria"],["az","994","Azerbaijan"],["bs","1","Bahamas"],["bh","973","Bahrain"],["bd","880","Bangladesh"],["bb","1","Barbados"],["by","375","Belarus"],["be","32","Belgium"],["bz","501","Belize"],["bj","229","Benin"],["bm","1","Bermuda"],["bt","975","Bhutan"],["bo","591","Bolivia"],["ba","387","Bosnia and Herzegovina"],["bw","267","Botswana"],["br","55","Brazil"],["io","246","British Indian Ocean Territory"],["vg","1","British Virgin Islands"],["bn","673","Brunei"],["bg","359","Bulgaria"],["bf","226","Burkina Faso"],["bi","257","Burundi"],["kh","855","Cambodia"],["cm","237","Cameroon"],["ca","1","Canada"],["cv","238","Cape Verde"],["bq","599","Caribbean Netherlands"],["ky","1","Cayman Islands"],["cf","236","Central African Republic"],["td","235","Chad"],["cl","56","Chile"],["cn","86","China"],["cx","61","Christmas Island"],["cc","61","Cocos (Keeling) Islands"],["co","57","Colombia"],["km","269","Comoros"],["cg","242","Congo (Brazzaville)"],["cd","243","Congo (Kinshasa)"],["ck","682","Cook Islands"],["cr","506","Costa Rica"],["hr","385","Croatia"],["cu","53","Cuba"],["cw","599","Curaçao"],["cy","357","Cyprus"],["cz","420","Czech Republic"],["ci","225","Côte d'Ivoire"],["dk","45","Denmark"],["dj","253","Djibouti"],["dm","1","Dominica"],["do","1","Dominican Republic"],["ec","593","Ecuador"],["eg","20","Egypt"],["sv","503","El Salvador"],["gq","240","Equatorial Guinea"],["er","291","Eritrea"],["ee","372","Estonia"],["sz","268","Eswatini"],["et","251","Ethiopia"],["fk","500","Falkland Islands (Malvinas)"],["fo","298","Faroe Islands"],["fj","679","Fiji"],["fi","358","Finland"],["fr","33","France"],["gf","594","French Guiana"],["pf","689","French Polynesia"],["ga","241","Gabon"],["gm","220","Gambia"],["ge","995","Georgia"],["de","49","Germany"],["gh","233","Ghana"],["gi","350","Gibraltar"],["gr","30","Greece"],["gl","299","Greenland"],["gd","1","Grenada"],["gp","590","Guadeloupe"],["gu","1","Guam"],["gt","502","Guatemala"],["gg","44","Guernsey"],["gn","224","Guinea"],["gw","245","Guinea-Bissau"],["gy","592","Guyana"],["ht","509","Haiti"],["hn","504","Honduras"],["hk","852","Hong Kong SAR China"],["hu","36","Hungary"],["is","354","Iceland"],["in","91","India"],["id","62","Indonesia"],["ir","98","Iran"],["iq","964","Iraq"],["ie","353","Ireland"],["im","44","Isle of Man"],["il","972","Israel"],["it","39","Italy"],["jm","1","Jamaica"],["jp","81","Japan"],["je","44","Jersey"],["jo","962","Jordan"],["kz","7","Kazakhstan"],["ke","254","Kenya"],["ki","686","Kiribati"],["xk","383","Kosovo"],["kw","965","Kuwait"],["kg","996","Kyrgyzstan"],["la","856","Laos"],["lv","371","Latvia"],["lb","961","Lebanon"],["ls","266","Lesotho"],["lr","231","Liberia"],["ly","218","Libya"],["li","423","Liechtenstein"],["lt","370","Lithuania"],["lu","352","Luxembourg"],["mo","853","Macao SAR China"],["mg","261","Madagascar"],["mw","265","Malawi"],["my","60","Malaysia"],["mv","960","Maldives"],["ml","223","Mali"],["mt","356","Malta"],["mh","692","Marshall Islands"],["mq","596","Martinique"],["mr","222","Mauritania"],["mu","230","Mauritius"],["yt","262","Mayotte"],["mx","52","Mexico"],["fm","691","Micronesia"],["md","373","Moldova"],["mc","377","Monaco"],["mn","976","Mongolia"],["me","382","Montenegro"],["ms","1","Montserrat"],["ma","212","Morocco"],["mz","258","Mozambique"],["mm","95","Myanmar (Burma)"],["na","264","Namibia"],["nr","674","Nauru"],["np","977","Nepal"],["nl","31","Netherlands"],["nc","687","New Caledonia"],["nz","64","New Zealand"],["ni","505","Nicaragua"],["ne","227","Niger"],["ng","234","Nigeria"],["nu","683","Niue"],["nf","672","Norfolk Island"],["kp","850","North Korea"],["mk","389","North Macedonia"],["mp","1","Northern Mariana Islands"],["no","47","Norway"],["om","968","Oman"],["pk","92","Pakistan"],["pw","680","Palau"],["ps","970","Palestinian Territories"],["pa","507","Panama"],["pg","675","Papua New Guinea"],["py","595","Paraguay"],["pe","51","Peru"],["ph","63","Philippines"],["pl","48","Poland"],["pt","351","Portugal"],["pr","1","Puerto Rico"],["qa","974","Qatar"],["ro","40","Romania"],["ru","7","Russia"],["rw","250","Rwanda"],["re","262","Réunion"],["ws","685","Samoa"],["sm","378","San Marino"],["sa","966","Saudi Arabia"],["sn","221","Senegal"],["rs","381","Serbia"],["sc","248","Seychelles"],["sl","232","Sierra Leone"],["sg","65","Singapore"],["sx","1","Sint Maarten"],["sk","421","Slovakia"],["si","386","Slovenia"],["sb","677","Solomon Islands"],["so","252","Somalia"],["za","27","South Africa"],["kr","82","South Korea"],["ss","211","South Sudan"],["es","34","Spain"],["lk","94","Sri Lanka"],["bl","590","St. Barthélemy"],["sh","290","St. Helena"],["kn","1","St. Kitts & Nevis"],["lc","1","St. Lucia"],["mf","590","St. Martin"],["pm","508","St. Pierre & Miquelon"],["vc","1","St. Vincent & Grenadines"],["sd","249","Sudan"],["sr","597","Suriname"],["sj","47","Svalbard & Jan Mayen"],["se","46","Sweden"],["ch","41","Switzerland"],["sy","963","Syria"],["st","239","São Tomé & Príncipe"],["tw","886","Taiwan"],["tj","992","Tajikistan"],["tz","255","Tanzania"],["th","66","Thailand"],["tl","670","Timor-Leste"],["tg","228","Togo"],["tk","690","Tokelau"],["to","676","Tonga"],["tt","1","Trinidad & Tobago"],["tn","216","Tunisia"],["tr","90","Turkey"],["tm","993","Turkmenistan"],["tc","1","Turks & Caicos Islands"],["tv","688","Tuvalu"],["vi","1","U.S. Virgin Islands"],["ug","256","Uganda"],["ua","380","Ukraine"],["ae","971","United Arab Emirates"],["gb","44","United Kingdom"],["uy","598","Uruguay"],["uz","998","Uzbekistan"],["vu","678","Vanuatu"],["va","39","Vatican City"],["ve","58","Venezuela"],["vn","84","Vietnam"],["wf","681","Wallis & Futuna"],["eh","212","Western Sahara"],["ye","967","Yemen"],["zm","260","Zambia"],["zw","263","Zimbabwe"],["ax","358","Åland Islands"]];

  function flagEmoji(iso2) {
    return String.fromCodePoint.apply(null, iso2.toUpperCase().split('').map(function (c) {
      return 0x1F1E6 + c.charCodeAt(0) - 65;
    }));
  }

  /* Populate an in-field country <select> + flag; returns getter for the
     selected [iso2, dial, name] entry. Defaults to US. */
  function initCountrySelect(selectEl, flagEl) {
    var selected = COUNTRIES[0];
    COUNTRIES.forEach(function (c, i) {
      var opt = document.createElement('option');
      opt.value = String(i);
      opt.textContent = flagEmoji(c[0]) + ' ' + c[2] + ' (+' + c[1] + ')';
      selectEl.appendChild(opt);
    });
    selectEl.value = '0';
    flagEl.textContent = flagEmoji(selected[0]);
    selectEl.addEventListener('change', function () {
      selected = COUNTRIES[parseInt(selectEl.value, 10)] || COUNTRIES[0];
      flagEl.textContent = flagEmoji(selected[0]);
    });
    return function () { return selected; };
  }

  function validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
  }

  /* Normalize a raw phone for the given dial code; returns national digits
     or null if invalid. NANP gets the strict area-code/exchange check. */
  function normalizePhone(raw, dial) {
    var digits = (raw || '').replace(/\D/g, '');
    if (digits.indexOf(dial) === 0 && digits.length > dial.length + 4) digits = digits.slice(dial.length);
    if (dial !== '1') digits = digits.replace(/^0+/, ''); // strip trunk 0 (e.g. UK 07911...)
    var valid = dial === '1'
      ? /^[2-9]\d{2}[2-9]\d{6}$/.test(digits)
      : /^\d{4,14}$/.test(digits);
    return valid ? digits : null;
  }

  window.UDKCapture = {
    readState: readState,
    writeState: writeState,
    subscribe: subscribe,
    flushOutbox: flushOutbox,
    COUNTRIES: COUNTRIES,
    flagEmoji: flagEmoji,
    initCountrySelect: initCountrySelect,
    validateEmail: validateEmail,
    normalizePhone: normalizePhone
  };

  flushOutbox();

  // This file loads with `defer` while Shopify's section-JS bundle is ALSO
  // deferred (injected in <head>, so it may execute FIRST). Sections guard
  // with: if (window.UDKCapture) init(); else listen for this event.
  document.dispatchEvent(new CustomEvent('udk:capture:ready'));
})();
